import React, { useMemo, useCallback } from 'react';
import styled from 'styled-components';

import qs from 'query-string';
import MUIMenuItem from '@material-ui/core/MenuItem';
import {
  bindPopover,
  bindTrigger,
  usePopupState,
} from 'material-ui-popup-state/hooks';

import xor from 'lodash/xor';
import filter from 'lodash/filter';
import castArray from 'lodash/castArray';
import { Popover } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';

import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';

import {
  Genre,
  Country,
  useGenres,
  useCountries,
  style,
  SwiperFilter,
} from '../api';

import Flex from './common/Flex';
import { Check } from './common/Icons';
import { InnerLink } from './common/Link';
import { TextRegular } from './common/Text';

import BPMFilter from './Filter/BPMFilter';

const MenuItem = withStyles(() => ({
  root: {
    padding: '16px',
    justifyContent: 'space-between',

    '& span': {
      opacity: 0.65,
      color: style.black_new,
      lineHeight: 'initial',
    },

    '& svg': {
      marginLeft: 16,
      color: style.primary_pink_new,
    },
  },

  selected: {
    backgroundColor: 'transparent !important',

    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
    },

    '& span': {
      opacity: 1,
      fontWeight: 'bold',
    },
  },
}))(MUIMenuItem);

const FilterValue = styled(TextRegular)`
  text-decoration: underline;
`;

const FilterItem = styled(Flex)`
  cursor: pointer;
  padding: 1.3rem 1.5rem;
  margin-right: 0px;

  :hover {
    background-color: whitesmoke;
  }
`;

export const useExploreQuery = (): SwiperFilter => {
  const { query } = useRouter();

  const currentQuery = useMemo(
    () =>
      qs.parse(qs.stringify(query, { skipNull: true }), {
        /**
         * It would be better to witch to arrayFormat: 'comma' since it
         * results in a more lean query.
         *
         * Also that format is used for sending data to the API, so why not keep
         * the same standard
         */
        arrayFormat: 'none',
        parseNumbers: true,
      }),
    [query],
  );

  return currentQuery;
};

export const useExploreQueryGenres = () => {
  const currentQuery = useExploreQuery();

  return useMemo(() => castArray(currentQuery.genre).filter(Boolean), [
    currentQuery,
  ]);
};

export const useExploreQueryCountries = () => {
  const currentQuery = useExploreQuery();

  return useMemo(() => castArray(currentQuery.country).filter(Boolean), [
    currentQuery,
  ]);
};

const GenresFilter = () => {
  const { t } = useTranslation('common');
  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'genre-filter',
    disableAutoFocus: true,
  });

  const genres = useGenres({ withActiveTracksOnly: true });

  const currentQuery = useExploreQuery();

  const selectedGenres = useExploreQueryGenres();

  const selectedGenresString = useMemo(() => {
    const list = filter(genres, ({ id }) => selectedGenres.includes(id));

    if (!list.length) {
      return 'All';
    }

    return list.length > 1 ? `${list.length} selected` : list[0].name;
  }, [selectedGenres, genres]);

  const renderMenuItem = useCallback(
    ({ id, name }: Genre) => {
      const selected = selectedGenres.includes(id);

      const nextSearch = qs.stringify(
        {
          ...currentQuery,
          genre: xor(selectedGenres, [id]),
        },
        { skipNull: true },
      );

      return (
        <InnerLink
          key={`genre-${id}`}
          href={{
            search: nextSearch,
          }}
        >
          <MenuItem selected={selected}>
            {name}
            {selected && <Check size={18} />}
          </MenuItem>
        </InnerLink>
      );
    },
    [currentQuery, selectedGenres],
  );

  const resetFilterHref = useMemo(
    () => ({
      search: qs.stringify(
        {
          ...currentQuery,
          genre: null,
        },
        { skipNull: true },
      ),
    }),
    [currentQuery],
  );

  return (
    <>
      <FilterItem mr={4}>
        <TextRegular color="grey_main_new" mr={2}>
          {t('common:genre') as string}
        </TextRegular>

        <FilterValue color="black" {...bindTrigger(popupState)}>
          {selectedGenresString}
        </FilterValue>
      </FilterItem>

      <Popover
        {...bindPopover(popupState)}
        anchorOrigin={{
          vertical: 32,
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Flex width="350px" maxHeight="400px" flexDirection="column">
          <InnerLink href={resetFilterHref}>
            <MenuItem selected={!selectedGenres.length}>
              <span>{t('common:all') as string}</span>
              {!selectedGenres.length && <Check size={18} />}
            </MenuItem>
          </InnerLink>
          <Flex width="100%" height="100%" flexDirection="column">
            {genres.map(renderMenuItem)}
          </Flex>
        </Flex>
      </Popover>
    </>
  );
};

const CountriesFilter = () => {
  const { t } = useTranslation('common');
  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'country-filter',
    disableAutoFocus: true,
  });

  const { result: countries } = useCountries();

  const currentQuery = useExploreQuery();

  const selectedCountries = useExploreQueryCountries();

  const selectedCountriesString = useMemo(() => {
    const list = filter(countries, ({ id }) => selectedCountries.includes(id));

    if (!list.length) {
      return 'All';
    }

    return list.length > 1 ? `${list.length} selected` : list[0].name;
  }, [selectedCountries, countries]);

  const renderMenuItem = useCallback(
    ({ id, name }: Country) => {
      const selected = selectedCountries.includes(id);

      const nextSearch = qs.stringify(
        {
          ...currentQuery,
          country: xor(selectedCountries, [id]),
        },
        { skipNull: true },
      );

      return (
        <InnerLink
          key={`country-${id}`}
          href={{
            search: nextSearch,
          }}
        >
          <MenuItem selected={selected}>
            {name}
            {selected && <Check size={18} />}
          </MenuItem>
        </InnerLink>
      );
    },
    [currentQuery, selectedCountries],
  );

  const resetFilterHref = useMemo(
    () => ({
      search: qs.stringify(
        {
          ...currentQuery,
          country: null,
        },
        { skipNull: true },
      ),
    }),
    [currentQuery],
  );

  return (
    <>
      <FilterItem mr={4}>
        <TextRegular color="grey_main_new" mr={2}>
          {t('common:country') as string}
        </TextRegular>

        <FilterValue color="black" {...bindTrigger(popupState)}>
          {selectedCountriesString}
        </FilterValue>
      </FilterItem>

      <Popover
        {...bindPopover(popupState)}
        anchorOrigin={{
          vertical: 32,
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Flex width="350px" maxHeight="400px" flexDirection="column">
          <InnerLink href={resetFilterHref}>
            <MenuItem selected={!selectedCountries.length}>
              <span>{t('common:all') as string}</span>
              {!selectedCountries.length && <Check size={18} />}
            </MenuItem>
          </InnerLink>
          <Flex width="100%" height="100%" flexDirection="column">
            {countries.map(renderMenuItem)}
          </Flex>
        </Flex>
      </Popover>
    </>
  );
};

const BPMFilterMenu = () => {
  const { t } = useTranslation('common');
  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'bpm-filter',
    disableAutoFocus: true,
  });

  const router = useRouter();

  const { query, pathname, push } = router;

  const currentQuery = useExploreQuery();

  const onChangeQueryKey = (key: string, value: string | number) =>
    push(
      {
        pathname,
        query: {
          ...query,
          [key]: value,
        },
      },
      undefined,
      { shallow: true },
    );

  const onBPMValues = (values: { minValue: number; maxValue: number }) => {
    if (currentQuery.min_bpm !== values.minValue) {
      onChangeQueryKey('min_bpm', values.minValue);
    }

    if (currentQuery.max_bpm !== values.maxValue) {
      onChangeQueryKey('max_bpm', values.maxValue);
    }
  };

  return (
    <>
      <FilterItem mr={4} {...bindTrigger(popupState)}>
        <TextRegular color={style.grey_main_new} mr={2}>
          {t('common:bpm') as string}
        </TextRegular>

        {!!currentQuery.min_bpm && !!currentQuery.max_bpm ? (
          <TextRegular color="black">
            {[currentQuery.min_bpm, currentQuery.max_bpm]
              .filter(Boolean)
              .join(' - ')}
          </TextRegular>
        ) : (
          <FilterValue>{t('common:all') as string}</FilterValue>
        )}
      </FilterItem>
      <Popover
        {...bindPopover(popupState)}
        anchorOrigin={{
          vertical: 32,
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Flex width="350px" px={4} py={3}>
          <BPMFilter
            onChange={(values) => onBPMValues(values)}
            initialMinValue={
              query.min_bpm !== undefined ? Number(query.min_bpm) : undefined
            }
            initialMaxValue={
              query.max_bpm !== undefined ? Number(query.max_bpm) : undefined
            }
          />
        </Flex>
      </Popover>
    </>
  );
};

const ExploreFilter = () => {
  const { t } = useTranslation('common');
  const router = useRouter();

  const { pathname } = router;

  return (
    <Flex>
      <GenresFilter />
      <CountriesFilter />
      <BPMFilterMenu />
      <InnerLink href={{ pathname }}>
        <FilterItem>
          <TextRegular color={style.grey_main_new}>
            {t('common:reset') as string}
          </TextRegular>
        </FilterItem>
      </InnerLink>
    </Flex>
  );
};

export default ExploreFilter;
